import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query'
import {
  getBonusStats,
  getUserTasks,
  getReferralUsers,
  claimRewards,
  getTaskTemplates,
  type ClaimRewardsRequest,
  type TasksResponse,
} from '../api/bonusApi'
import { getRefetchInterval } from '../utils/polling'
import { useAppContext } from '../contexts/App.context'

// ============= Query Hooks =============

/**
 * Fetch bonus stats for a wallet
 */
export function useBonusStats() {
  const { canFetchUserData } = useAppContext()

  return useQuery({
    queryKey: ['bonus', 'stats'],
    queryFn: async () => {
      return await getBonusStats()
    },
    enabled: canFetchUserData,
    staleTime: 30_000, // 30 seconds
    refetchInterval: getRefetchInterval(), // Refetch every 60 seconds
  })
}

/**
 * Fetch user tasks
 */
export function useUserTasks(): ReturnType<typeof useQuery<TasksResponse>> {
  const { canFetchUserData } = useAppContext()

  return useQuery({
    queryKey: ['bonus', 'tasks'],
    queryFn: async () => await getUserTasks(),
    enabled: canFetchUserData,
    staleTime: 30_000, // 30 seconds
    refetchInterval: getRefetchInterval(), // Refetch every 60 seconds
  })
}

/**
 * Fetch task templates without authentication (for unauthenticated users)
 */
export function useTaskTemplates(): ReturnType<typeof useQuery<TasksResponse>> {
  return useQuery({
    queryKey: ['bonus', 'task-templates'],
    queryFn: async () => await getTaskTemplates(),
    staleTime: 5 * 60_000, // 5 minutes (templates change less frequently)
    refetchInterval: getRefetchInterval(),
  })
}

/**
 * Fetch referral users with infinite scroll pagination
 */
export function useReferralUsers(limit = 50, offset = 0) {
  const { canFetchUserData } = useAppContext()

  return useInfiniteQuery({
    queryKey: ['bonus', 'referral'],
    queryFn: async () => {
      return await getReferralUsers(limit, offset)
    },
    enabled: canFetchUserData,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (sum, page) => sum + page.referrals.length,
        0,
      )
      if (loadedCount >= lastPage.pagination.total) {
        return undefined
      }
      return loadedCount
    },
    initialPageParam: 0,
    staleTime: 30_000, // 60 seconds (less frequently changing)
    refetchInterval: getRefetchInterval(), // Refetch every 60 seconds
  })
}

// ============= Mutation Hooks =============

/**
 * Claim rewards mutation
 */
export function useClaimRewards() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: ClaimRewardsRequest) =>
      await claimRewards(request),

    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: ['bonus', 'stats', variables.walletAddress],
      })

      queryClient.invalidateQueries({
        queryKey: ['bonus', 'tasks', variables.walletAddress],
      })

      queryClient.invalidateQueries({ queryKey: ['bonus'] })
    },
  })
}
